import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import T from 'i18n';
import CustomViewerButton from 'features/common/CustomViewerButton';
import CustomViewerMultiActionButton from 'features/common/CustomViewerMultiActionButton';
import Tooltip from 'features/common/SimpleTooltip';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { faChromecast } from '@fortawesome/free-brands-svg-icons';
import {
  selectPermissions,
  selectSessionStarted,
  selectTheatreData,
  selectUserId,
  selectPresentQuery,
} from 'common/selectors';
import qs from 'qs';
import { playerActions } from './enums';
import InfoText from '../InfoText';
import { TooltipWrapper } from '../TooltipWrapper';
import { useSendMessages } from './useSendMessages';
import { useLeaveAGroup } from '../redux/leaveAGroup';

interface VideoViewerToolboxProps {
  fileId: string;
  hearingRoomMode?: 'private' | 'public';
  playerRef: React.RefObject<any>;
  isTheatreMode?: boolean;
  pauseTheatreMode?: () => void;
  presenter?: any;
  playerAction?: string;
  updatePlayerState?: (state: any) => void;
  startPlayerPresenting?: () => void;
}

const VideoViewerToolbox: React.FC<VideoViewerToolboxProps> = ({
  fileId,
  hearingRoomMode,
  playerRef,
  presenter,
  playerAction,
  updatePlayerState = () => {},
  startPlayerPresenting = () => {},
}) => {
  const { leaveAGroup } = useLeaveAGroup();

  // Redux selectors
  const userId = useSelector(selectUserId);
  const permissions = useSelector(selectPermissions);
  const hearingRoomStarted = useSelector(selectSessionStarted);
  const present = useSelector(selectPresentQuery);
  const rawTheatreData = useSelector((state: any) => selectTheatreData(state, hearingRoomMode));
  const hubConnectionId = useSelector((state: any) => state.auth.hubConnectionId);

  const theatreData = React.useMemo(() => rawTheatreData || { messages: [] }, [rawTheatreData]);
  const isPresenting = !!present;
  const isTheatreMode = !!hearingRoomMode;

  const hasBeenPresenting = useRef(false);

  const prevPresenterConnectionIdRef = useRef(null);
  const prevHearingRoomStartedRef = useRef(null);

  const { sendFullMessage, startStopPresentHandler } = useSendMessages({
    startPlayerPresenting,
    present,
    fileId,
  });

  useEffect(() => {
    hasBeenPresenting.current = hasBeenPresenting.current || isPresenting;
  }, [isPresenting]);

  // Mount/unmount logic
  useEffect(() => {
    if (isPresenting) {
      startStopPresentHandler(present, true);
    }
    if (isTheatreMode) {
      updatePlayerState({ videoAction: playerActions.PAUSE, videoTime: 0 });
    }
    return () => {
      const newUrlQuery =
        qs.parse(window.location.search && window.location.search.substr(1)) || {};

      if (hasBeenPresenting.current && !newUrlQuery.present) {
        leaveAGroup('present');
      }
    };
    // eslint-disable-next-line
  }, []);

  // React in EV to present state changes
  useEffect(() => {
    // Theatre video action/time
    const { videoAction: theatreVideoAction, videoTime: theatreVideoTime = 0 } =
      (theatreData.messages &&
        theatreData.messages.find(({ fileId: fId }: any) => fId === fileId)) || {
        videoAction: playerActions.PAUSE,
        videoTime: 0,
      };

    if (
      isTheatreMode &&
      playerRef.current &&
      (theatreVideoAction || theatreVideoTime !== undefined)
    ) {
      playerRef.current.seekTo(theatreVideoTime);
      updatePlayerState({
        videoAction: theatreVideoAction,
      });
    }
  }, [isTheatreMode, theatreData.messages, fileId]);

  // Update video state/time when player action changes
  useEffect(() => {
    if (
      isPresenting &&
      playerRef.current &&
      playerAction &&
      playerAction !== playerActions.SHOWCONTROLS &&
      playerAction !== playerActions.HIDECONTROLS
    ) {
      const playedSeconds = Math.round(playerRef.current.getCurrentTime() || 0);
      sendFullMessage(playerAction, playedSeconds);
    }
  }, [fileId, playerAction, playerRef, sendFullMessage, isPresenting]);

  // Stop presenting if not the presenter
  useEffect(() => {
    const presenterConnectionId = (theatreData.messages as any[]).find(
      (message) => message && message.fileId === fileId && userId === message.presenterId,
    )?.connectionId;

    if (prevPresenterConnectionIdRef.current !== presenterConnectionId) {
      const shouldIBePresenting = presenterConnectionId === hubConnectionId;

      if (prevPresenterConnectionIdRef.current && !shouldIBePresenting && isPresenting)
        startStopPresentHandler();

      prevPresenterConnectionIdRef.current = presenterConnectionId;
    }
  }, [
    fileId,
    hubConnectionId,
    isPresenting,
    startStopPresentHandler,
    theatreData.messages,
    userId,
  ]);

  // Stop presenting if the hearing room has been stopped
  useEffect(() => {
    if (
      present === 'public' &&
      prevHearingRoomStartedRef.current !== hearingRoomStarted &&
      !hearingRoomStarted
    ) {
      if (
        ['paused', 'started'].includes(prevHearingRoomStartedRef.current as unknown as string) &&
        hearingRoomStarted === 'stopped'
      ) {
        startStopPresentHandler();
      }
      prevHearingRoomStartedRef.current = hearingRoomStarted;
    }
  }, [hearingRoomStarted, present, startStopPresentHandler]);

  // Render
  return (
    <div className="viewing-custom-viewer-toolbox">
      <div className="document-options top-right">
        {isTheatreMode ? (
          <InfoText presenter={presenter} fileId={fileId} />
        ) : isPresenting ? (
          <TooltipWrapper isPresenting>
            <CustomViewerButton
              icon={faChromecast}
              icon2={present === 'private' ? faLock : undefined}
              className={classnames({ present: !!present })}
              onClick={() => {
                startStopPresentHandler();
              }}
            />
          </TooltipWrapper>
        ) : (
          (permissions.hearingRoom.canPresent('private') ||
            permissions.hearingRoom.canPresent('public')) && (
            <CustomViewerMultiActionButton direction="down">
              <CustomViewerButton icon={faChromecast} onClick={() => {}} />
              <Tooltip title={T.translate('case.presentToPrivateRoomTooltip')}>
                <CustomViewerButton
                  icon={faChromecast}
                  icon2={faLock}
                  onClick={() => {
                    startStopPresentHandler('private');
                  }}
                  disabled={!permissions.hearingRoom.canPresent('private')}
                />
              </Tooltip>
              <Tooltip title={T.translate('case.presentToPublicRoomTooltip')}>
                <CustomViewerButton
                  icon={faChromecast}
                  onClick={() => startStopPresentHandler('public')}
                  disabled={!permissions.hearingRoom.canPresent('public')}
                />
              </Tooltip>
            </CustomViewerMultiActionButton>
          )
        )}
      </div>
    </div>
  );
};

export default VideoViewerToolbox;
